/**
 * Breite Tabellen passend verkleinern statt seitlich scrollen zu lassen.
 *
 * CSS allein schafft das nicht: Spalten werden nur so schmal wie ihr
 * schmalstes unteilbares Stück (eine Zahl, ein Bruch), und zwölf solcher
 * Spalten sind in einer halben iPad-Breite schlicht zu breit. Dann wird die
 * ganze Tabelle gleichmäßig kleiner geschrieben – Polster und Formeln hängen
 * an `em`, sie schrumpfen mit. Unter `MIN_SCALE` wird es unlesbar; dort bleibt
 * als letzter Ausweg der Scrollbereich der Tabelle selbst.
 */

const MIN_SCALE = 0.6;
/** Schriftgröße der Tabelle laut markdown.css (`.markdown-body table`). */
const BASE_EM = 0.95;

const lastWidth = new WeakMap<Element, number>();

function fit(box: HTMLElement): void {
  // Beim Neurendern ersetzte Tabellen melden sich ein letztes Mal (Größe 0) –
  // dann loslassen, sonst hielte der Beobachter jede alte Tabelle am Leben.
  if (!box.isConnected) {
    observer?.unobserve(box);
    lastWidth.delete(box);
    return;
  }
  const table = box.querySelector<HTMLElement>(":scope > table");
  const width = box.clientWidth;
  if (!table || !width || lastWidth.get(box) === width) return;
  lastWidth.set(box, width);
  table.style.fontSize = "";
  let need = table.offsetWidth;
  if (need <= width + 1) return;
  // Zweimal: Rahmenlinien schrumpfen nicht mit, der erste Schritt landet knapp daneben.
  let scale = 1;
  for (let i = 0; i < 2 && need > width + 1; i++) {
    scale = Math.max(MIN_SCALE, (scale * width) / need);
    table.style.fontSize = `${(BASE_EM * scale).toFixed(3)}em`;
    need = table.offsetWidth;
  }
}

const observer =
  typeof ResizeObserver === "undefined"
    ? null
    : new ResizeObserver((entries) => {
        for (const e of entries) fit(e.target as HTMLElement);
      });

/**
 * Tabellen in diesen Knoten anpassen – sofort, falls sie schon im Dokument
 * hängen (der PDF-Export rastert direkt danach), und danach bei jeder
 * Größenänderung erneut.
 */
export function fitTables(roots: Iterable<Element>): void {
  for (const root of roots) {
    const boxes = root.classList.contains("md-table-scroll")
      ? [root]
      : Array.from(root.querySelectorAll(".md-table-scroll"));
    for (const box of boxes as HTMLElement[]) {
      if (box.isConnected) fit(box);
      observer?.observe(box);
    }
  }
}
